import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Text,
  Alert,
  AlertIcon,
  AlertDescription,
  VStack,
  Divider,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { pxToRem, colorTheme, useHandleRubricAnswerChange } from '@codametrix/ui-common';
import { RubricQuestionField } from './rubric-question-field';
import { findAnswerForQuestion, getQuestionIdsToClear, SpanScopedWorkflowMatch } from './rubric-utils';
import {
  useWorkflowEvaluation,
  EvaluateWorkflowFn,
} from './useWorkflowEvaluation';
import { useRubricAutofill } from './useRubricAutofill';

const noopAttributeChange = (_attribute: CQA.UpdateAttributeParams) => {};
const noopDeleteAttribute = async (_params: CQA.DeleteAttributeParams) => {};

/**
 * Props that drive a single source-scoped section inside the highlight drawer.
 * One section is rendered per concrete SPANS answer attribute whose answer
 * references the active span.
 */
export interface SpanScopedWorkflowSectionProps {
  match: SpanScopedWorkflowMatch;
  /** The active span the drawer is showing. */
  span: Accelerate.SpanAnnotation;
  /** All spans in the case — needed by SPAN/SPANS RubricQuestionField rendering inside the scoped workflow. */
  allSpans?: Accelerate.SpanAnnotation[];
  /** Backend workflow evaluator (shared with the rest of the drawer). */
  evaluateFn?: EvaluateWorkflowFn;
  workItemId?: number;
  rubricVersionId?: number;
  /** Persist a source-scoped span answer. */
  onAttributeChange?: (attribute: CQA.UpdateAttributeParams) => void;
  onDeleteAttribute?: (params: CQA.DeleteAttributeParams) => Promise<void>;
  onClearDownstream?: (params: CQA.ClearDownstreamAttributesParams) => void;
  editable?: boolean;
}

/**
 * Renders source-scoped SPAN workflow questions for the active span. State is
 * isolated by addressing every attribute read/write to the span entity scoped by
 * the concrete source SPANS attribute id.
 */
export const SpanScopedWorkflowSection: React.FC<SpanScopedWorkflowSectionProps> = ({
  match,
  span,
  allSpans,
  evaluateFn,
  workItemId,
  rubricVersionId,
  onAttributeChange,
  onDeleteAttribute,
  onClearDownstream,
  editable = true,
}) => {
  const { sourceQuestion, sourceAttribute, spanWorkflow, workflowQuestions } = match;
  const spanScopeAttributeId = sourceAttribute.id!;

  // Gate source-scoped questions behind case-level completion (mirrors the all-span workflow gate).
  const caseLevelComplete = useSelector(
    (state: any) => state.gradeBenchPanel?.caseLevelComplete,
  );
  const caseAttributes = useSelector(
    (state: any) => state.gradeBenchPanel?.caseAttributes,
  ) as CQA.Attribute[] | undefined;
  const scopedQuestionsDisabled = caseLevelComplete === false;

  const scopedAttributes = (span.rubricAttributes || []).filter(
    (a) => (a.spanScopeAttributeId ?? a.parentQuestionId) === spanScopeAttributeId,
  );

  const {
    evaluation,
    visibleQuestions,
    filteredAnswersMap,
    autofilledAnswersMap,
    isLoading,
    error,
  } = useWorkflowEvaluation({
    workflowId: spanWorkflow.id,
    attributes: scopedAttributes,
    workflowQuestions,
    codeContext: match.codeContext,
    workItemId,
    evaluateFn: evaluateFn!,
    enabled: !!evaluateFn,
    rubricVersionId,
    caseAttributes: caseAttributes || undefined,
    triggerSourceAttributes: match.ownerCode?.attributes,
    spanScopeAttributeId,
    spanId: span.id,
    codeId: match.ownerCode?.codeId != null ? String(match.ownerCode.codeId) : undefined,
  });

  const handleAnswerChangeWithCleanup = useHandleRubricAnswerChange(
    onAttributeChange || noopAttributeChange,
    onDeleteAttribute || noopDeleteAttribute,
    onClearDownstream,
  );

  const handleAnswerChangeRef = useRef<(questionId: number, value: number | number[] | string) => void>(() => {});

  const { trackAnswerChange, clearDownstreamTracking } = useRubricAutofill({
    entityId: String(span.id),
    entityKey: `span-${span.id}-scope-${spanScopeAttributeId}`,
    attributes: scopedAttributes,
    autofilledAnswers: autofilledAnswersMap,
    onAutofill: (questionId, answerValueId) => {
      handleAnswerChangeRef.current(questionId, answerValueId);
    },
  });

  const [lastChangedQuestionId, setLastChangedQuestionId] = useState<number | null>(null);

  const handleAnswerChange = useCallback(
    async (questionId: number, value: number | number[] | string) => {
      if (scopedQuestionsDisabled) return;
      if (!onAttributeChange) return;

      setLastChangedQuestionId(questionId);

      const downstreamIds = getQuestionIdsToClear(visibleQuestions, scopedAttributes, questionId);

      const isFreeText = typeof value === 'string';
      const isArray = Array.isArray(value);
      if (!isFreeText && !isArray) {
        trackAnswerChange(questionId, value);
      }
      clearDownstreamTracking(downstreamIds);

      const question = visibleQuestions.find((wq) => wq.question.id === questionId)?.question;
      const isSpan = question?.questionType === 'SPAN';
      const isSpans = question?.questionType === 'SPANS';

      let answerFields: Record<string, unknown>;
      if (isFreeText) {
        answerFields = { answerDisplayText: value };
      } else if (isSpan || isSpans) {
        answerFields = { spanAnnotationIds: value };
      } else {
        answerFields = { answerValueId: value };
      }

      await handleAnswerChangeWithCleanup({
        entityId: String(span.id),
        entityType: 'span' as CQA.AttributeEntityType,
        attributes: scopedAttributes,
        questionId,
        ...answerFields,
        downstreamQuestionIds: downstreamIds,
        spanScopeAttributeId,
      });
    },
    [scopedQuestionsDisabled, onAttributeChange, span.id, spanScopeAttributeId, visibleQuestions, scopedAttributes, handleAnswerChangeWithCleanup, trackAnswerChange, clearDownstreamTracking],
  );

  handleAnswerChangeRef.current = handleAnswerChange;

  const sectionTitle =
    sourceQuestion.questionText ||
    sourceQuestion.name ||
    spanWorkflow.name ||
    'Source-scoped Highlight Questions';

  if (error) {
    return (
      <Box>
        <Text fontSize={pxToRem(16)} fontWeight="semibold" color={colorTheme.text.highEmphasis} mb={2}>
          {sectionTitle}
        </Text>
        <Alert status="error" variant="left-accent" borderRadius="md">
          <AlertIcon />
          <AlertDescription fontSize="sm">
            Unable to load per-span questions. Please try refreshing the page.
          </AlertDescription>
        </Alert>
        <Divider my={4} />
      </Box>
    );
  }

  if (isLoading && !evaluation) {
    return (
      <Box>
        <Text fontSize={pxToRem(16)} fontWeight="semibold" color={colorTheme.text.highEmphasis} mb={2}>
          {sectionTitle}
        </Text>
        <Center py={4}>
          <Spinner size="md" />
        </Center>
        <Divider my={4} />
      </Box>
    );
  }

  if (!visibleQuestions.length) {
    return null;
  }

  return (
    <Box>
      <Text
        fontSize={pxToRem(16)}
        fontWeight="semibold"
        color={colorTheme.text.highEmphasis}
        mb={2}
      >
        {sectionTitle}
      </Text>
      {scopedQuestionsDisabled && (
        <Alert
          status="info"
          variant="left-accent"
          borderRadius="md"
          mb={2}
          sx={{ display: 'flex !important', flexDirection: 'row !important', alignItems: 'center !important' }}
        >
          <AlertIcon mr={2} flexShrink={0} />
          <AlertDescription fontSize="sm">
            Please complete all case-level questions before answering per-span questions.
          </AlertDescription>
        </Alert>
      )}
      <VStack spacing={3} align="stretch">
        {visibleQuestions.map((wq) => (
          <RubricQuestionField
            key={wq.question.id}
            workflowQuestion={wq}
            currentAnswer={findAnswerForQuestion(scopedAttributes, wq.question.id)}
            onAnswerChange={(value) => handleAnswerChange(wq.question.id, value)}
            isDisabled={scopedQuestionsDisabled || !editable || isLoading}
            isAutofilled={autofilledAnswersMap.has(wq.question.id)}
            filteredAnswerIds={filteredAnswersMap.get(wq.question.id)}
            spans={allSpans}
            isEvaluating={isLoading && lastChangedQuestionId === wq.question.id}
          />
        ))}
      </VStack>
      <Divider my={4} />
    </Box>
  );
};

export default SpanScopedWorkflowSection;
